/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2022 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/
// @delete
import queryString from "query-string";

import { clientLogger } from "src/lib/logging/datadog";

export type HTTPMethod = "GET" | "POST" | "DELETE" | "PUT" | "PATCH";

export interface APICall {
  url: string;
  method: HTTPMethod;
  data?: any;
  onStart?: any;
  onSuccess: any;
  onFail?: any;
  onFailToast?: (msgBackend: string) => string;
  onSuccessToast?: (msgBackend: string) => string;
}

export const api = async (method: HTTPMethod, url?: string, variables?: Record<string, any>): Promise<any> => {
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : "";
  const headers = {
    "Content-Type": "application/json",
    authorization: `Bearer ${token}`,
    ...(process.env.ENVIRONMENT === "local" ? { mode: "no-cors" } : {}), // use no-cors if running locally for ngrok
  };

  const options = {
    method,
    headers,
    body: method !== "GET" ? JSON.stringify(variables) : undefined,
  };

  if (method === "GET" && variables) {
    url += `?${queryString.stringify(variables)}`;
    if (JSON.stringify(variables) === "{}") {
      clientLogger.warn("'variables' was passed as an empty object, should be undefined");
    }
  }

  const response = await fetch(`${process.env.API_URL}${url}`, options);

  if (response.status >= 200 && response.status <= 299) {
    return response.text().then((text) => (text ? JSON.parse(text) : {}));
  } else {
    const error = new Error(response.statusText);
    error.name = response.status.toString();
    error.message = response.statusText;
    throw error;
  }
};

const apiDefault = {
  get: <TResponse = unknown>(...args: any[]): Promise<TResponse> => api("GET", ...args),
  post: <TResponse = unknown>(...args: any[]): Promise<TResponse> => api("POST", ...args),
  patch: <TResponse = unknown>(...args: any[]): Promise<TResponse> => api("PATCH", ...args),
  put: <TResponse = unknown>(...args: any[]): Promise<TResponse> => api("PUT", ...args),
  delete: <TResponse = unknown>(...args: any[]): Promise<TResponse> => api("DELETE", ...args),
};

export default apiDefault;
